<template>
  <div class="rounded bg-white mx-6">
    <span class="w-full py-2 flex flex-col md:flex-row justify-between">
      <p class="w-full md:w-3/4 md:px-4 py-4 text-grey-darker break-words">
        Use this section to announce your restaurant as closed during special occasions.
        You can even close your restaurant for a certain time of the day.
      </p>
      <span
        class="w-full md:w-1/4 py-4 flex justify-end"
      >
        <pt-button
          class="w-full md:w-auto px-4"
          variant="primary-outline"
          @action="addHoliday"
        >
          Add Holiday
        </pt-button>
      </span>
    </span>
    <div class="flex flex-col">
      <pt-view-table
        :data="specialHolidayList"
        :columns="columns"
        :row-class="classes.row"
        :header-class="classes.header"
        :column-class="classes.column"
        :tbody-class="classes.tbody"
        :thead-class="classes.thead"
        class="promo-table border mb-8"
      />
      <pt-loader
        v-if="isLoading"
        icon-class="fa-refresh fa-5x text-white"
        container="body"
      />

    </div>
    <add-edit-holidays
      ref="sidebar"
      :is-edit="isEdit"
      :holiday-details="selectedSpecialHoliday"
      @closedAddEditSidebar="closedAddEditSidebar"
      @fetchHolidaysList="fetchSpecialHoliday"
    />
    <response-modal
      ref="responseModal"
      :is-delete="true"
      @onConfirm="deleteHoliday"
    />
  </div>
</template>

<script>
import { PtViewTable } from '@pathao/ui-components/esm/Table';
import apiRequest from '@src/utils/apiRequest';
import { SPECIAL_HOLIDAYS_LIST, DELETE_SPECIAL_HOLIDAYS_BY_ID } from '@src/config/apiEndpoints';
import formatDate from '@src/utils/format-date';
import formatTime from '@src/utils/format-time';
import AddEditHolidays from './add-edit-holidays.vue';
import ResponseModal from '@components/common/responseModal.vue';

let parentThis;
const columns = [
  {
    header: 'Title',
    accessor: 'name',
  },
  {
    header: 'Holiday Starts',
    component: {
      props: ['rowData'],
      computed: {
        getFormattedDate() {
          return parentThis.getDate(this.rowData.start_date);
        },
      },
      render() {
        return (
          <span>
            {this.getFormattedDate}
          </span>
        );
      },
    },
  },
  {
    header: 'Holiday Ends',
    component: {
      props: ['rowData'],
      computed: {
        getFormattedDate() {
          return parentThis.getDate(this.rowData.end_date);
        },
      },
      render() {
        return (
          <span>
            {this.getFormattedDate}
          </span>
        );
      },
    },
  },
  {
    header: 'Full Day Closed?',
    component: {
      props: ['rowData'],
      computed: {
        fullDayClosedStyle() {
          return this.rowData.is_full_day_closed ? 'success-status rounded px-4 py-1' : 'failed-status rounded px-4 py-1';
        },
        isFullDayClosedText() {
          return this.rowData.is_full_day_closed ? 'YES' : 'NO';
        },
      },
      render() {
        return (
          <span class={this.fullDayClosedStyle}>
            {this.isFullDayClosedText}
          </span>
        );
      },
    },
  },
  {
    header: 'Opening Hours',
    component: {
      props: ['rowData'],
      computed: {
        getFormattedTime() {
          return parentThis.getTime(this.rowData.opening_time);
        },
      },
      render() {
        return (
          <span>
            {this.getFormattedTime}
          </span>
        );
      },
    },
  },
  {
    header: 'Closing Hours',
    component: {
      props: ['rowData'],
      computed: {
        getFormattedTime() {
          return parentThis.getTime(this.rowData.closing_time);
        },
      },
      render() {
        return (
          <span>
            {this.getFormattedTime}
          </span>
        );
      },
    },
  },
  {
    header: 'Status',
    component: {
      props: ['rowData'],
      computed: {
        statusStyle() {
          return this.rowData.status === 'PENDING' ? 'pending-status rounded px-4 py-1'
            : this.rowData.status === 'APPROVED' ? 'success-status rounded px-4 py-1'
              : 'failed-status rounded px-4 py-1';
        },
        statusText() {
          return this.rowData.status.toLowerCase();
        },
      },
      render() {
        let statusStyle = ['font-bold capitalize', this.statusStyle];
        return (
          <span class={statusStyle}>
            {this.statusText}
          </span>
        );
      },
    },
  },
  {
    header: 'Action',
    component: {
      props: ['rowData'],
      computed: {
        isPendingHoliday() {
          return this.rowData.status === 'PENDING';
        },
      },
      methods: {
        editItem() {
          this.$sendEvent('RS_EditPendingHoliday');
          parentThis.editHoliday(this.rowData);
        },
        deleteItem() {
          this.$sendEvent('RS_DeletePendingHoliday');
          parentThis.showDeleteModal(this.rowData.id);
        },
      },
      render() {
        let actionsStyle = this.isPendingHoliday ? 'flex items-start justify-center' : 'hidden';
        return (
          <div class={actionsStyle}>
            <div
              class="border cursor-pointer rounded-sm py-1 px-2 mr-2"
              on={{
                click: e => {
                  this.editItem();
                } }}
            >
              <i class="fa fa-pencil text-grey-dark" />
            </div>
            <div
              class="border cursor-pointer rounded-sm py-1 px-2"
              on={{
                click: e => {
                  this.deleteItem();
                } }}
            >
              <i class="fa fa-trash text-grey-dark" />
            </div>
          </div>
        );
      },
    },
  },
];
export default {
  name: 'SpecialHolidaysList',
  page: {
    title: 'SpecialHolidaysList',
  },
  components: {
    PtViewTable,
    AddEditHolidays,
    ResponseModal,
  },
  props: {
    selectedIndex: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      columns,
      specialHolidayList: [],
      selectedSpecialHoliday: null,
      isEdit: false,
      isLoading: false,
      classes: {
        thead: 'bg-grey-lighter w-full md:hidden-thead',
        tbody: 'bg-white text-center',
        row: 'border-b-4 lg:border-b border-solid border-grey-light mb-0 w-full',
        column: 'p-4 border-b lg:border-b-0',
        header: 'py-4',
      },
      deleteHolidayId: null,
    };
  },
  watch: {
    async selectedIndex() {
      await this.fetchSpecialHoliday();
    },
  },
  async mounted() {
    parentThis = this;
    await this.fetchSpecialHoliday();
  },
  methods: {
    getDate(date) {
      return formatDate(date, 'DD/MM/YYYY');
    },
    getTime(date) {
      return date ? formatTime(date) : '';
    },
    addHoliday() {
      this.$sendEvent('RS_AddNewHoliday');
      this.openAddEditSidebar();
    },
    openAddEditSidebar() {
      this.$refs.sidebar.toggle();
    },
    editHoliday(holidayDetails) {
      this.selectedSpecialHoliday = holidayDetails;
      this.isEdit = true;
      this.openAddEditSidebar();
    },
    closedAddEditSidebar() {
      this.selectedSpecialHoliday = null;
      this.isEdit = false;
    },
    showDeleteModal(id) {
      this.$refs.responseModal.showModal();
      this.deleteHolidayId = id;
    },
    async deleteHoliday() {
      this.$sendEvent('RS_ConfirmDeleteHoliday');
      const id = this.deleteHolidayId;
      try {
        this.isLoading = true;
        await apiRequest({
          url: DELETE_SPECIAL_HOLIDAYS_BY_ID.replace(':id', id),
          method: 'DELETE',
        });
        this.$notification.success('Successfully deleted Special Holiday request');
        this.fetchSpecialHoliday();
      } catch (error) {
        this.$notification.error('Failed to delete the Special Holiday request');
      } finally {
        this.isLoading = false;
        this.deleteHolidayId = null;
      }
    },
    async fetchSpecialHoliday() {
      try {
        this.isLoading = true;
        const response = await apiRequest({
          url: SPECIAL_HOLIDAYS_LIST,
          method: 'GET',
        });
        this.specialHolidayList = response.data.data;
      } catch (error) {
        this.$notification.error('Failed to fetch Special Holidays list');
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
